/** What a user types in chat to hand a question to Ask instead of to the workspace. */
export const ASK_CHAT_COMMAND = '/ask';

/** Doubling the slash sends the text as an ordinary message; see `unescapeAskChatCommand`. */
const ESCAPED_PREFIX = `/${ASK_CHAT_COMMAND}`;

export interface AskChatCommand {
  /** The question after the command, empty when the command was typed on its own. */
  prompt: string;
}

/**
 * Recognizes `/ask` at the start of a chat message, or returns undefined for anything that is
 * an ordinary message.
 *
 * The command has to be the whole of the beginning — `/ask` followed by whitespace or nothing —
 * so a message that merely mentions it ("`/asking` for a friend") is not one. Leading
 * whitespace is allowed, since a stray space before the slash is a typo rather than a decision
 * to send a message.
 *
 * `/ask` with nothing after it is still the command: the user asked for Ask, they just have not
 * typed the question yet, and opening the panel with an empty composer is what they asked for.
 */
export function parseAskChatCommand(text: string): AskChatCommand | undefined {
  const trimmed = text.trimStart();
  if (isEscapedAskChatCommand(trimmed) || !trimmed.toLowerCase().startsWith(ASK_CHAT_COMMAND)) {
    return undefined;
  }
  const rest = trimmed.slice(ASK_CHAT_COMMAND.length);
  // Anything other than a break after the command makes it a different word.
  if (rest !== '' && !/^\s/.test(rest)) {
    return undefined;
  }
  return { prompt: rest.trim() };
}

/**
 * Removes the escape from a message that opted out of the command, so `//ask Sam about Friday`
 * is sent as the message the user meant. Every other message is returned untouched.
 *
 * Without this there would be no way to send a message that starts with the command, and "ask
 * someone to ask someone" is a normal thing to say in a workspace.
 */
export function unescapeAskChatCommand(text: string): string {
  const leadingWhitespace = text.slice(0, text.length - text.trimStart().length);
  const trimmed = text.trimStart();
  return isEscapedAskChatCommand(trimmed) ? `${leadingWhitespace}${trimmed.slice(1)}` : text;
}

function isEscapedAskChatCommand(trimmedText: string): boolean {
  return trimmedText.toLowerCase().startsWith(ESCAPED_PREFIX);
}
